"use client";

import Link from "next/link";

import { authClient } from "../../lib/auth/client";

const LINK =
  "rounded-full px-4 py-2.5 text-paper/85 hover:bg-ink-alt hover:text-paper";

/**
 * A client component because the nav changes with the session, and the header
 * is rendered inside client pages (app/reading/new) which cannot import a
 * server component. The session read is cached by Better Auth's store, so
 * navigating between pages does not re-fetch it.
 */
export default function SiteHeader() {
  const { data: session, isPending } = authClient.useSession();
  const isAdmin =
    (session?.user as { role?: string | null } | undefined)?.role === "admin";

  return (
    <header className="sticky top-0 z-40 bg-ink/90 backdrop-blur">
      <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-5 sm:px-8">
        <Link href="/" className="font-heading text-2xl text-paper">
          Syft
          <span aria-hidden className="text-blue">
            .
          </span>
        </Link>
        {/* Library stays visible at every width: it is the only way back to
            your own readings, and the header is sticky, so hiding it below
            the breakpoint stranded phone users on whatever page they were
            on. Pricing and Admin are secondary, and can wait for the room. */}
        <nav
          aria-label="Main"
          className="flex items-center gap-1 whitespace-nowrap font-body text-base sm:gap-2"
        >
          <Link href="/library" className={LINK}>
            Library
          </Link>
          <Link href="/#pricing" className={`hidden sm:inline-block ${LINK}`}>
            Pricing
          </Link>
          {isAdmin && (
            <Link href="/admin" className={`hidden sm:inline-block ${LINK}`}>
              Admin
            </Link>
          )}
          {/* Nothing renders while the session is still loading: a "Sign in"
              link that flips to "Account" a moment later reads as a bug. */}
          {!isPending &&
            (session ? (
              <>
                <Link
                  href="/glossary"
                  className={`hidden sm:inline-block ${LINK}`}
                >
                  Glossary
                </Link>
                <Link href="/account" className={LINK}>
                  Account
                </Link>
              </>
            ) : (
              <Link href="/sign-in" className={LINK}>
                Sign in
              </Link>
            ))}
          {/* Signed out, making an account is the thing most people came
              for, so it sits beside the upload action rather than in the
              grey nav. */}
          {!isPending && !session && (
            <Link href="/sign-up" className={`hidden sm:inline-block ${LINK}`}>
              Create account
            </Link>
          )}
          <Link
            href="/reading/new"
            aria-label="Upload a reading"
            className="ml-1 bg-blue px-5 py-3 font-semibold text-blue-deep hover:-translate-y-0.5 hover:opacity-90"
          >
            <span className="sm:hidden">Upload</span>
            <span className="hidden sm:inline">Upload a reading</span>
          </Link>
        </nav>
      </div>
    </header>
  );
}
